import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v3';
import type { AccountContact } from '@/api/commercial/accounts';
import { type CreditAnalysis, creditAnalysisApi } from '@/api/commercial/credit-analysis';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

const contactItemSchema = z.object({
  id: z.string(),
  type: z.enum(['Celular', 'Telefone Residencial', 'Telefone Comercial', 'Email']),
  contact: z.string(),
});

const contactSchema = z.object({
  id: z.string(),
  name: z.string(),
  contactItems: z.array(contactItemSchema),
});

function validateEmail(value?: string) {
  if (!value) return true;
  const emails = value
    .split(/[,;]+/)
    .map((e) => e.trim())
    .filter((e) => e);
  const emailValidator = z.string().email();
  const response = emails.map((email) => emailValidator.safeParse(email));
  const someError = response.some(({ success }) => success === false);
  return !someError;
}

const resendDocumentFormSchema = z
  .object({
    emails: z
      .string()
      .optional()
      .refine((value) => validateEmail(value), 'Email inválido!'),
    contacts: z.array(contactSchema).optional(),
  })
  .refine(
    (data) => {
      const hasEmails = data.emails && data.emails.trim() !== '';
      const hasContacts = data.contacts && data.contacts.length > 0;
      return hasEmails || hasContacts;
    },
    {
      message: 'Informe ao menos um email ou selecione ao menos um contato',
      path: ['emails'],
    },
  );

export type ResendDocumentSchema = z.infer<typeof resendDocumentFormSchema>;

interface ResendDocumentRequestDialogProps {
  open: boolean;
  onClose: () => void;
  proposalId: string;
  accountId?: string;
  contacts?: AccountContact[];
  recipients?: string[];
  creditAnalysisId: string;
  onSuccess?: (creditAnalysis: CreditAnalysis) => void;
}

export function ResendDocumentRequestDialog({
  open,
  onClose,
  proposalId,
  accountId,
  contacts = [],
  recipients = [],
  creditAnalysisId,
  onSuccess,
}: ResendDocumentRequestDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const contactEmails = contacts.flatMap((contact) =>
    (contact.contactItems ?? [])
      .filter((item) => item.type === 'Email')
      .map((item) => item.contact),
  );

  const displayRecipients = recipients.length > 0 ? recipients : contactEmails;

  const contactsSelected = contacts.filter((contact) =>
    (contact.contactItems ?? []).some(
      (item) => item.type === 'Email' && displayRecipients.includes(item.contact),
    ),
  );

  const externalEmails = displayRecipients.filter((email) => !contactEmails.includes(email));

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ResendDocumentSchema>({
    resolver: zodResolver(resendDocumentFormSchema),
    defaultValues: {
      emails: '',
      contacts: [],
    },
  });

  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      reset({
        emails: externalEmails.join('; '),
        contacts: contactsSelected,
      });
      setIsEdit(false);
      wasOpen.current = true;
    } else if (!open) {
      wasOpen.current = false;
    }
  }, [open, contactsSelected, externalEmails, reset]);

  const onSubmit = async (data: ResendDocumentSchema) => {
    setIsLoading(true);

    try {
      const contactEmails = (data.contacts ?? []).flatMap((contact) =>
        contact.contactItems.filter((item) => item.type === 'Email').map((item) => item.contact),
      );

      const parsedEmails = (data.emails || '')
        .split(/[,;]+/)
        .map((email) => email.trim())
        .filter((email) => email !== '');

      const allEmails = [...new Set([...contactEmails, ...parsedEmails])];

      await creditAnalysisApi.resendDocumentRequest({
        emails: allEmails,
        accountId: accountId!,
        proposalId,
        creditAnalysisId,
      });

      const updatedCreditAnalysis = await creditAnalysisApi.findOne(creditAnalysisId);

      toast({
        title: 'Sucesso',
        description: 'Solicitação de envio de documentos reenviada com sucesso!',
      });

      queryClient.invalidateQueries({
        queryKey: ['creditAnalysis'],
      });
      queryClient.invalidateQueries({
        queryKey: ['creditAnalysisTimeline'],
      });
      queryClient.invalidateQueries({
        queryKey: ['creditAnalysisTimeline', creditAnalysisId],
      });
      if (accountId) {
        queryClient.invalidateQueries({
          queryKey: ['creditAnalysis', 'account', accountId],
        });
        queryClient.invalidateQueries({
          queryKey: ['accountDocuments', accountId],
        });
      }

      if (updatedCreditAnalysis && onSuccess) {
        onSuccess(updatedCreditAnalysis);
      }

      onClose();
    } catch (error) {
      console.error('Erro ao reenviar solicitação:', error);
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro ao reenviar a solicitação. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto p-4">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Reenviar solicitação de documentação
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {!isEdit && (
            <section className="space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm text-gray-700 font-semibold mb-1">
                    Contatos para o reenvio:
                  </h3>

                  <div className="flex flex-wrap gap-2">
                    {displayRecipients.length > 0 ? (
                      displayRecipients.map((email) => (
                        <Badge key={email} variant="secondary" className="px-3 py-1">
                          {email}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">Nenhum contato encontrado</p>
                    )}
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="h-fit"
                  aria-label="Editar contatos"
                  onClick={() => setIsEdit(true)}
                >
                  <Pencil size={16} />
                </Button>
              </div>
            </section>
          )}

          {isEdit && (
            <section className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <h3 className="text-sm text-gray-700 font-semibold">Editar contatos:</h3>
                <Button type="button" variant="ghost" size="sm" onClick={() => setIsEdit(false)}>
                  Cancelar edição
                </Button>
              </div>

              <Controller
                name="contacts"
                control={control}
                render={({ field }) => (
                  <Combobox
                    type="multiple"
                    data={contacts}
                    label="Contatos"
                    behavior="search"
                    placeholder="Selecione um contato"
                    //@ts-expect-error
                    value={field.value ?? []}
                    keyExtractor={(item) => item.id}
                    displayValueGetter={(item: AccountContact) =>
                      item.contactItems?.find((i) => i.type === 'Email')?.contact ?? ''
                    }
                    onOptionChange={field.onChange}
                  />
                )}
              />

              <Controller
                name="emails"
                control={control}
                render={({ field }) => (
                  <div className="w-full">
                    <label
                      htmlFor="other-emails"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      Outros emails
                    </label>
                    <div className="mt-2">
                      <input
                        id="other-emails"
                        type="text"
                        className="block w-full px-3 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400  focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                        placeholder="example@mail.com; example2@mail.com"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Separe múltiplos emails com ponto e vírgula (;)
                      </p>
                      {errors.emails && (
                        <p className="text-xs text-red-600 mt-1">{errors.emails.message}</p>
                      )}
                    </div>
                  </div>
                )}
              />
            </section>
          )}

          <DialogFooter className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Reenviando...' : 'Reenviar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
